/**
 * Attendance wire types — sv_control's attendance engine at three widths, exactly as
 * sv_gateway serializes it (snake_case, int64 → number, google.protobuf.*Value → the
 * value or null).
 *
 *   GET /control/attendance                              → CompanyAttendanceReport
 *   GET /control/departments/{dept_id}/attendance        → DepartmentAttendanceReport
 *   GET /control/personnel/{record_id}/attendance        → PersonnelAttendanceReport
 *   GET /me/attendance                                   → PersonnelAttendanceReport
 *
 * The three are the SAME data at different widths, which is why they share
 * `AttendanceSummary` and `AttendanceDayRecord` here rather than each getting a
 * near-identical type: a company row and a person's day are the company row's own
 * numbers, and a screen that drills company → department → person should not have to
 * re-map anything on the way down.
 *
 * These types used to live in `entities/me/types.ts`, back when the self-scoped report
 * was the only one the cabinet called. That file now re-exports them so existing
 * importers are unaffected.
 *
 * Shapes verified against the live gateway (api.sam-vision.uz, 2026-08-12) rather than
 * inferred from the OpenAPI document, which describes these four responses in prose and
 * carries no schema for them.
 */
import type {AttendanceSchedule, PersonnelRecord} from '@/entities/personnel/types';

/**
 * The verdict sv_control's attendance engine stamps on a single day
 * (sv_control/crates/application/src/attendance.rs). A day can carry several at once
 * (e.g. `late` + `overtime`); `onTime` and `absent` are mutually exclusive with each
 * other but not asserted so here.
 */
export type AttendanceFlag =
  | 'absent'
  | 'unscheduled'
  | 'incomplete'
  | 'late'
  | 'earlyArrival'
  | 'earlyLeave'
  | 'overtime'
  | 'onTime';

/** Period roll-up. Every counter is a plain int64; minutes, not hours. */
export type AttendanceSummary = {
  scheduled_days: number;
  present_days: number;
  absent_days: number;
  on_time_days: number;
  late_days: number;
  early_arrival_days: number;
  early_leave_days: number;
  overtime_days: number;
  unscheduled_days: number;
  incomplete_days: number;
  total_worked_minutes: number;
};

/**
 * One in→out episode inside a day, built from the direction each terminal reports.
 * An episode still open at the end of the day (walked in, never registered leaving)
 * has `end_millis` and `minutes` null — which is what `incomplete` flags.
 */
export type AttendanceInterval = {
  start_millis: number;
  start_device_name: string | null;
  end_millis: number | null;
  end_device_name: string | null;
  minutes: number | null;
};

/**
 * One calendar day of a report.
 *
 * Two different durations live here and they are NOT the same number:
 *   `worked_minutes`   — first event to last, breaks INCLUDED.
 *   `interval_minutes` — the sum of `intervals`, breaks EXCLUDED.
 * A screen that shows one and labels it the other is the classic payroll dispute, so
 * the report UI names both.
 *
 * `flags` is the raw string[] — narrow to AttendanceFlag at the call site (an unknown
 * flag from a newer server must degrade, not crash).
 */
export type AttendanceDayRecord = {
  /**
   * Какой это день по графику и календарю (SV-54): `work`, `holiday`, `day_off`,
   * `rest`, `unscheduled`. Нет у ответов старого сервера.
   */
  day_kind?: string;
  /** Название праздника или выходного из календаря. */
  day_title?: string;
  date: string;
  weekday: number;
  scheduled: boolean;
  arrival_time_millis: number | null;
  departure_time_millis: number | null;
  arrival_device_name: string | null;
  departure_device_name: string | null;
  event_count: number;
  worked_minutes: number | null;
  arrival_delta_minutes: number | null;
  departure_delta_minutes: number | null;
  flags: string[];
  intervals?: AttendanceInterval[] | null;
  interval_minutes?: number | null;
};

/**
 * The same day record, as it arrives in a report covering MORE THAN ONE PERSON — the
 * person's identity is repeated onto every row because the rows are a flat list, not a
 * list per employee.
 */
export type AttendanceDayRow = AttendanceDayRecord & {
  record_id: string;
  full_name: string;
  employee_number: string;
  position: string;
  schedule_source?: string | null;
};

/** One person's line in a company- or department-wide report. */
export type AttendanceEmployeeSummary = {
  record_id: string;
  full_name: string;
  employee_number: string;
  position: string;
  schedule_source?: string | null;
  summary: AttendanceSummary;
  /**
   * Откреплён внутри периода (RFC 3339). Такой человек остаётся в отчёте за дни
   * до открепления — закрытый месяц не меняется задним числом. Нет поля — работает.
   */
  detached_at?: string | null;
};

/**
 * What every report says about the period it actually covers. `from`/`to` are always
 * filled; `month` only when the period happens to be a whole calendar month — which is
 * how a screen knows whether to title itself "Август 2026" or "01.08 — 14.08".
 */
export type ReportPeriodFields = {
  month?: string | null;
  from: string;
  to: string;
};

/** GET /control/personnel/{record_id}/attendance and GET /me/attendance. */
export type PersonnelAttendanceReport = ReportPeriodFields & {
  personnel: PersonnelRecord;
  schedule: AttendanceSchedule | null;
  schedule_source: string | null;
  summary: AttendanceSummary;
  days: AttendanceDayRecord[];
};

/** The control-side projection of a department, as the report echoes it back. */
export type ControlDepartment = {
  department_id: string;
  company_id: string;
  organization_department_id?: string | null;
  name: string;
  code: string;
  personnel_count: number;
  devices_count: number;
  telegram_chat_id?: string | null;
  attendance_schedule?: AttendanceSchedule | null;
};

/**
 * Тело `PATCH /control/departments/{department_id}/schedule` — единственный способ
 * задать график отдела снаружи.
 *
 * `camelCase`, в отличие от `AttendanceSchedule`, который приходит в ответе: это
 * протобуф, переименовывать поля он не умеет, поэтому запись и чтение здесь пишутся
 * по-разному. Маршрут принимает и `snake_case`, но одна форма на весь код — меньше
 * поводов однажды отправить `snake_case` туда, где синонимов нет.
 *
 * Все поля необязательны, и опущенное поле НЕ меняется. Форма отдела показывает их
 * все сразу, поэтому {@link updateDepartmentSchedule} шлёт их все — иначе выключенный
 * в форме флажок «время смены» не стёр бы старое время, а просто не упомянул бы его.
 *
 * ⚠️ **Оба конца задаются вместе.** `startMinutes` без `endMinutes` (или наоборот) —
 * `422`: половина графика хуже, чем его отсутствие, потому что отчёт начнёт считать
 * опоздания по домыслу о второй половине. Оба `null` — законное «время не задано»:
 * отдел задаёт только пороги.
 *
 * ⚠️ **Конец раньше начала — это нормально.** `startMinutes: 1260`,
 * `endMinutes: 360` — ночная смена с 21:00 до 6:00, и форма не должна это блокировать.
 */
export type ScheduleInput = {
  /** Минут от полуночи, `0..1440`, либо `null`. */
  startMinutes: number | null;
  endMinutes: number | null;
  /** Биты рабочих дней, бит 0 — понедельник (31 = пн–пт). `0..127`. */
  workdaysMask: number;
  /** Четыре порога, каждый `0..240` минут. */
  lateGraceMinutes: number;
  earlyArrivalThresholdMinutes: number;
  earlyLeaveGraceMinutes: number;
  overtimeThresholdMinutes: number;
  /**
   * Своё время отдельных дней (SV-55). Пусто — у всех дней общее. В теле
   * `PATCH …/schedule` сервер принимает плоский список.
   */
  dayTimes: DayTimeInput[];
};

/** Своё время одного дня недели: 0 — пн … 6 — вс, минуты от полуночи. */
export type DayTimeInput = {
  weekday: number;
  startMinutes: number;
  endMinutes: number;
};

/** GET /control/departments — the projection the attendance routes are keyed by. */
export type ControlDepartments = {departments: ControlDepartment[]};

/** GET /control/departments/{dept_id}/attendance. */
export type DepartmentAttendanceReport = ReportPeriodFields & {
  department: ControlDepartment;
  schedule: AttendanceSchedule | null;
  summary: AttendanceSummary;
  employees: AttendanceEmployeeSummary[];
  days: AttendanceDayRow[];
};

/**
 * GET /control/attendance.
 *
 * `days` is EMPTY unless the call asked for `include_days=true` — two hundred people
 * over a month is six thousand rows, and the summary table most screens draw needs
 * none of them.
 */
export type CompanyAttendanceReport = ReportPeriodFields & {
  summary: AttendanceSummary;
  employees: AttendanceEmployeeSummary[];
  days: AttendanceDayRow[];
};

/** An empty roll-up — the zero for client-side aggregation. */
export const EMPTY_SUMMARY: AttendanceSummary = {
  scheduled_days: 0,
  present_days: 0,
  absent_days: 0,
  on_time_days: 0,
  late_days: 0,
  early_arrival_days: 0,
  early_leave_days: 0,
  overtime_days: 0,
  unscheduled_days: 0,
  incomplete_days: 0,
  total_worked_minutes: 0,
};

/* ── Табель приходов и уходов ──────────────────────────────────────────────────────
 *
 * Матрица «человек × числа месяца», и, в отличие от трёх плоских листов
 * `/control/attendance/export`, состав колонок в ней заказывает вызывающий. Из чего
 * состоит заказ — рассказывает сам сервер (`/control/attendance/timesheet/fields`),
 * и это не вежливость, а способ не зашивать у себя список, который зависит от ПРАВ
 * читателя: у кого нет `salaries`, тот зарплатных ключей в справочнике не увидит
 * вовсе. Поэтому галочки строятся из ответа, а не из константы в этом файле —
 * новое поле появляется в кабинете без нашего релиза.
 */

/** Группа галочек: заголовок над своим блоком полей. */
export type TimesheetFieldGroup = {
  key: string;
  title: string;
};

/**
 * Одно поле или один лист.
 *
 * `locked` — снять нельзя: ФИО в матрице и лист расшифровки. Расшифровка держится
 * там не из упрямства: распечатанный табель без легенды букв, значков и допусков —
 * это будущий спор «а почему у меня другие цифры».
 *
 * `permission` — право, которым сервер это поле закрыл; приходит пустым у открытых.
 * Клиенту его проверять не нужно (чего нет в ответе, того нет и в галочках), но
 * знать полезно: по нему подписывается блок «доступно с правом …».
 */
export type TimesheetField = {
  key: string;
  group: string;
  title: string;
  defaultOn: boolean;
  locked: boolean;
  permission: string;
};

/** GET /control/attendance/timesheet/fields — из чего строится выбор. */
export type TimesheetCatalog = {
  groups: TimesheetFieldGroup[];
  fields: TimesheetField[];
  sheets: TimesheetField[];
};

/** Чем сортировать строки табеля. `department` — умолчание сервера. */
export type TimesheetSort = 'department' | 'name' | 'employeeNumber';

export const TIMESHEET_SORTS: readonly TimesheetSort[] = [
  'department',
  'name',
  'employeeNumber',
];
